import { Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, initializeAuth } from './store/authStore';
import { Layout } from './components/Layout/Layout';
import { LoadingSpinner } from './components/UI/LoadingSpinner';
import { useEffect, useState } from 'react';
import { ParallaxWrapper } from './components/ParallaxWrapper';
import { useTranslation } from 'react-i18next';

// Initialize i18n - using simple.ts system
import './i18n/simple';

// Pages
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import SportingPage from './pages/Sporting/SportingPage';
import SettingsPage from './pages/Settings/SettingsPage';
import PricingPage from './pages/Pricing/PricingPage';
import LandingPage from './pages/Landing/LandingPage';
import ServiciosPage from './pages/Public/ServiciosPage';
import ContactoPage from './pages/Public/ContactoPage';
import NosotrosPage from './pages/Public/NosotrosPage';
import PrivacyPolicyPage from './pages/Legal/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/Legal/TermsAndConditionsPage';
import CookiePolicyPage from './pages/Legal/CookiePolicyPage';
import TopNavDemo from './pages/Demo/TopNavDemo';


// Protected Layout Component
const ProtectedLayout: React.FC = () => {
  const { isAuthenticated, _hasHydrated, token, user } = useAuthStore();
  const [isInitializing, setIsInitializing] = useState(true);
  
  // Esperar a que se complete la hidratación
  if (!_hasHydrated) {
    return (
      <div className="min-h-screen bg-primary-900 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Initialize auth only when accessing protected routes
  useEffect(() => {
    const initialize = async () => {
      if (_hasHydrated && isInitializing) {
        await initializeAuth();
        setIsInitializing(false);
      }
    };
    
    initialize();
  }, [_hasHydrated, isInitializing]);

  // Show loading while initializing auth
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-primary-900 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  
  // Si no está autenticado O no tiene token O no tiene usuario, redirigir al login
  if (!isAuthenticated || !token || !user) {
    // Usar Navigate para redirección inmediata sin renderizar contenido protegido
    return <Navigate to="/login" replace />;
  }
  
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

const App: React.FC = () => {
  const { _hasHydrated } = useAuthStore();
  const location = useLocation();
  const { i18n } = useTranslation();

  // Handle language URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const langParam = params.get('lang');
    
    if (langParam && (langParam === 'es' || langParam === 'en')) {
      // Only change language if it's different from current
      if (i18n.language !== langParam) {
        i18n.changeLanguage(langParam);
      }
    }
  }, [location.search, i18n]);

  // Mostrar loading solo mientras se hidrata el store
  // No necesitamos inicializar auth aquí, se hará en ProtectedLayout cuando sea necesario
  if (!_hasHydrated) {
    return (
      <div className="min-h-screen bg-primary-900 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <ParallaxWrapper>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/servicios" element={<ServiciosPage />} />
        <Route path="/contacto" element={<ContactoPage />} />
        <Route path="/nosotros" element={<NosotrosPage />} />

        {/* Legal Routes */}
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />

        {/* Keep these routes accessible but not linked from navigation */}
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/demo/top-nav" element={<TopNavDemo />} />
        
        {/* Protected Routes con Layout */}
        <Route element={<ProtectedLayout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/sporting/*" element={<SportingPage />} />
          <Route path="/settings/*" element={<SettingsPage />} />
        </Route>
        
        {/* Catch all route - redirect to login for protected routes, 404 for others */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </ParallaxWrapper>
  );
};

export default App;
